import {
  allocationsTransformHelper,
  callsTransformHelper,
  editHistoryTransformHelper,
  sharingTransformHelper,
  trasncriptionTransformHelper,
} from "./responseTransformHelpers"

import { getEndPointArgs } from "hooks/useQueryUpdate"
import parentApi from "store/parentApi"
import { convertQueryObjToString } from "utils"
import { dateTimeFunction } from "utils/date"
import availablePaymentDiscountTransformHelper from "./availablePaymentDiscountTransformHelper"
import currentSharingsTransformHelper from "./currentSharingsTransformHelper"
import detailTransformHelper from "./detailTransformHelper"
import discountTransformHelper from "./discountTransformHelper"
import existingDiscountTransformHelper from "./discountTypeTransformer"
import getQueryTransformObject from "./getQueryTransformObject"
import { leadsTransformHelper } from "./leadsTransfromHelper"
import meetingTransformHelper from "./meetingTransformHelper"
import reviewDetailTransformHelper from "./reviewDetailTransformHelper"
import reviewObservationFieldTransformHelper from "./reviewObservationTransformHelper"
import taskLogsTransformHelper from "./taskLogsTransformHelper"
import { ClientWorkflowsTransformer } from "./clientWorkflowsTransformer"
import { leadCategoriesTransformer } from "./leadCategoriesTransformer"
import { leadLabelsTransformer } from "../../../shared/api/transformResponse/leadLabelsTransformer"
import { leadsFilterCategoriesTransformer } from "./leadsFilterCategoriesTransformer"
import { whatsappChatInsightsTransformer } from "./whatsappChatInsightsTransformer"
import { clientInsightsTransformer } from "./clientInsightsTransformer"
import { clientEngagementScoresTransformer } from "./clientEngagementScoresTransformer"
import { clientSegmentsFilterTransformer } from "./clientSegmentsFilterTransformer"
import { clientConversationTimelineTransformer } from "./clientConversationTimelineTransformer"
import { clientOverallScoresTransformer } from "./clientOverallScoresTransformer"
const clientDetailApi = parentApi.injectEndpoints({
  endpoints: build => ({
    // Get Client Detail
    getClientDetail: build.query({
      query: clientId => `/clients/${clientId}?profile=true`,
      transformResponse: response => detailTransformHelper(response),
      providesTags: ["client-detail"],
    }),

    // Get Client Leads
    getLeads: build.query({
      query: p => {
        const params = { ...p }

        if (params.status) {
          params.leadTask = params.leadTask ? "all-time-task" : "last-task"
        }

        if (params.activeTab) {
          delete params["activeTab"]
        }

        return `internalInquiries/search?leadStatusFilterType=0&${convertQueryObjToString(
          getQueryTransformObject(params)
        )}`
      },
      transformResponse: (response, meta, args) => leadsTransformHelper(response, args),
      providesTags: (resp, error, args) => {
        return args.clientId ? [{ type: "leads", id: args.clientId }] : ["leads"]
      },
    }),

    // Get Client Tasks
    getTaskLogs: build.query({
      query: ({ clientId, filterObject }) => `/clients/${clientId}/tasks${"?" + convertQueryObjToString(filterObject)}`,
      transformResponse: response => taskLogsTransformHelper(response),
      providesTags: ["taskLogs"],
    }),

    // Get Client Calls
    getCallLogs: build.query({
      query: params => `/phoneCalls?${convertQueryObjToString(params)}`,
      transformResponse: response => callsTransformHelper(response),
    }),

    getCallDetail: build.query({
      query: ({ taskId, taskType }) => `/phoneCalls/${taskId}/transcription?entityType=${taskType}`,
      transformResponse: (res, _meta, args) => trasncriptionTransformHelper(res, args),
    }),

    // Get Client Allocations
    getAllocationLogs: build.query({
      query: params => `/clients/getAllocationLogs/${params.clientId}?${convertQueryObjToString(params.filter)}`,
      transformResponse: response => allocationsTransformHelper(response),
      providesTags: ["allocationLogs"],
    }),

    // Get Client Sharing
    getSharingLogs: build.query({
      query: params => `/clients/getLeadSharingLog/${params.clientId}?${convertQueryObjToString(params.filter)}`,
      transformResponse: response => sharingTransformHelper(response),
    }),

    // Get Client Edit Log
    getEditLogs: build.query({
      query: params => `/clientLogs/client/${params.clientId}?${convertQueryObjToString(params.filter)}`,
      transformResponse: response => editHistoryTransformHelper(response),
      providesTags: ["editLogs"],
    }),

    getCurrentLeadSharing: build.query({
      query: leadId => `/internalInquiries/sharings?leadIds=${leadId}`,
      transformResponse: response => {
        const transformedData = currentSharingsTransformHelper(response)
        return transformedData
      },
      providesTags: ["sharings"],
    }),

    editClient: build.mutation({
      query: ({ client, mobileNumberViewed, ...values }) => {
        const newCells =
        mobileNumberViewed && values?.mobileNumber?.filter(Boolean).map(e => ({ number: e, short: e.slice(4) }))
        const payload = {
          name: values.name || values.pocName,
          gender: values.gender,
          ...(mobileNumberViewed && newCells?.length && { cells: newCells }),
          ...(!!values.fatherName && { relationship: values.fatherName }),
          phone: values.phone,
          ...(!!values.dateOfBirth && {
            dateOfBirth: dateTimeFunction({ date: values.dateOfBirth.toDate(), timeZone: true }),
          }),
          address: values.address,
          countryId: values.country.id,
          cityId: values?.city.id,
          ...(!!values.locality && { localityId: values.locality?.id }),
          ...(!!values.passportNumber && { passport: values.passportNumber }),
          nationalId: values.cnic,
          ...(values.email?.trim() && { email: values.email }),
          clientUserType: values.clientType,
          clientSubType: values.clientSubType,
          clientType: values?.clientType,
          ...(values.companyName && { companyName: values.companyName }),
          ...(values.pocName && { pocName: values.pocName }),
          ...(values?.clientClass === "affiliate" && { referralId: values?.searchAffiliate?.affiliateId }),
          referralType: values?.clientClass,
          comments: values.reason,
        }

        return { url: `/clients/${client.id}`, method: "PUT", body: payload }
      },
      invalidatesTags: (result, error) => {
        return result ? ["client-detail", "editLogs"] : []
      },
    }),

    addLead: build.mutation({
      query: leadValues => {
        const { client, classification, leadType, project, propertyType, source, subType, salesEvent, addedWithEmail } =
          leadValues
        const isInvestmentLead = leadType === "investment"
        const getClientPayload = client => {
          return client?.id
            ? { clientId: client.id }
            : {
                client: {
                  allocatedTo:
                    client?.clientClass == "affiliate"
                      ? client?.searchAffiliate?.user?.id
                      : leadValues?.allocatedTo?.id,
                  name: client.name || client.pocName,
                  gender: client.gender,
                  cells: client?.mobileNumber?.filter(Boolean).map(number => ({ number, short: number.slice(4) })),
                  ...(!!client.fatherName && { relationship: client.fatherName }),
                  countryId: client.country?.id,
                  cityId: client.city?.id,
                  localityId: client.locality?.id,
                  ...(client.email?.trim() && { email: client.email }),
                  clientUserType: client.clientType,
                  referralType: client?.clientClass,
                  ...(client?.clientClass == "affiliate" && { referralId: client?.searchAffiliate?.affiliateId }),
                  referralSource: leadValues?.referralSource,
                  previousInteraction: leadValues?.previousInteraction === "true" ? 1 : 0,
                  ...(leadValues?.previousInteractionNumber && {
                    previousInteractionNumber: leadValues?.previousInteractionNumber,
                  }),
                  clientReferred: leadValues?.isClientReferred === "true" ? 1 : 0,
                  ...(leadValues?.referralDetails && { referralDetails: leadValues?.referralDetails }),
                  clientType: client?.clientType,
                  clientSubType: client?.clientSubType,
                  ...(client?.companyName && { companyName: client.companyName }),
                  ...(client?.pocName && { pocName: client.pocName }),
                  addedWithEmail: !!addedWithEmail,
                },
              }
        }
        const payload = {
          ...getClientPayload(client),
          ...(!isInvestmentLead && classification && { classificationId: classification }),
          ...(project?.value && { projectId: project?.value }),
          ...(project?.value && { objectType: "project" }),
          ...(!isInvestmentLead && propertyType?.value && { propertyType: propertyType.value }),
          //          "leadSide": "demand",
          ...(!isInvestmentLead && propertyType?.value && { parentTypeId: propertyType.value }),
          //          "clientSelected": "",
          //          "purposeId": 1,
          sourcePlatform: "propforce_web_app",
          inquirySourceParentIds: source.value,
          source: "propforce_web_app",
          ...(!isInvestmentLead && subType?.value && { childTypeId: subType.value }),
          interestedIn: leadType,
          ...(client?.id && { userId: client?.User.userId }),
          firmState: "on",
          ...(salesEvent && {
            salesEventId: salesEvent,
            module: "clients",
            checkIn: true,
            meetingWithProcess: "manual",
            userId:
              client?.clientClass == "affiliate"
                ? client?.searchAffiliate?.user?.id
                : client.allocatedTo?.id || client?.userId,
          }),
        }
        return {
          url: `/internalInquiries`,
          method: "POST",
          body: payload,
        }
      },
      invalidatesTags: (resp, error) => (resp ? ["leads", "client-detail", "clients"] : []),
    }),

    addShareWithSales: build.mutation({
      query: ({ sharingInfo, userIds, leads }) => {
        const payload = {
          sharingInfo,
          userIds,
          inquiryIds: leads.map(item => item.id),
          unShareOldUsers: false,
        }
        return {
          url: `/internalInquiries/shareWithSales`,
          method: "PUT",
          body: payload,
        }
      },
      invalidatesTags: (_resp, error) => (!error ? ["sharings", "leads"] : []),
    }),

    addUnShareWithSales: build.mutation({
      query: params => {
        const payload = {
          inquiryIds: params.leads.map(item => item.id),
          ...(params?.userId && { fromUserId: params?.userId }),
        }
        return {
          url: `/internalInquiries/unshare`,
          method: "PUT",
          body: payload,
        }
      },
      invalidatesTags: (_resp, error) => (!error ? ["sharings", "leads"] : []),
    }),

    shareLeadWithSupportManual: build.mutation({
      query: values => {
        const payload = {
          inquiryIds: values.leads.map(item => item.id),
          designationId: values?.user?.designationId,
          selectedSupportUser: values?.user?.id,
          unShareOldUsers: values.unShareOldUsers,
          purposeId: values?.sharingPurpose,
        }
        return {
          url: `/internalInquiries/shareWithSupportManual/user/${values?.user?.id}`,
          method: "PUT",
          body: payload,
        }
      },
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),

    shareLeadWithSupportAuto: build.mutation({
      query: values => {
        const payload = {
          inquiryIds: values.leads.map(item => item.id),
          designationId: values?.user?.designationId,
          unShareOldUsers: values.unShareOldUsers,
          purposeId: values?.sharingPurpose,
        }
        return { url: `/internalInquiries/shareWithSupportAuto`, method: "PUT", body: payload }
      },
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),

    shareLeadWithFractionalManual: build.mutation({
      query: values => {
        const payload = {
          inquiryIds: values?.leads?.map(item => item?.id),
        }
        return {
          url: `/internalInquiries/shareWithFractional/user/${values?.user?.id}`,
          method: "PUT",
          body: payload,
        }
      },
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),

    shareLeadWithFractionalAuto: build.mutation({
      query: values => {
        const payload = {
          inquiryIds: values?.leads?.map(item => item?.id),
        }
        return { url: `/internalInquiries/shareWithFractionalAuto`, method: "PUT", body: payload }
      },
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),


    unshareLeadFromSupport: build.mutation({
      query: values => {
        const payload = {
          inquiryIds: values.leads.map(item => item.id),
          ...(!!values?.userId && { fromUserId: values?.userId }),
        }
        return {
          url: `/internalInquiries/unshare-support`,
          method: "PUT",
          body: payload,
        }
      },
      invalidatesTags: (_resp, error) => (!error ? ["leads"] : []),
    }),

    markFavoriteUnFavorite: build.mutation({
      query: ({ clientIds, favorite }) => {
        const payload = { clientIds }
        return {
          url: favorite ? `/favoriteClients/favorite` : `/favoriteClients/unfavorite`,
          method: "PUT",
          body: payload,
        }
      },
      async onQueryStarted({ clientIds, favorite }, { getState, dispatch, queryFulfilled }) {
        const state = getState()
        const { parentApi, app } = state
        const listingQueryArgs = getEndPointArgs("getClients", parentApi)
        const detailQueryArgs = getEndPointArgs("getClientDetail", parentApi)
        const leadDetailQueryArgs = getEndPointArgs("getLeads", parentApi)
        const listingPatchResult =
          listingQueryArgs &&
          dispatch(
            clientDetailApi.util.updateQueryData("getClients", listingQueryArgs, draftClients => {
              draftClients.data.forEach(e => {
                if (clientIds.includes(e.id)) {
                  if (favorite) {
                    e.isFavoriteOf.push(app.user.info.id)
                  } else {
                    e.isFavoriteOf = e.isFavoriteOf.filter(userId => userId !== app.user.info.id)
                  }
                }
              })
            })
          )

        const detailPatchResult =
          detailQueryArgs &&
          dispatch(
            clientDetailApi.util.updateQueryData("getClientDetail", detailQueryArgs, clientDraft => {
              if (favorite) {
                clientDraft.ClientFavorite.push(app.user.info.id)
              } else {
                clientDraft.ClientFavorite = clientDraft.ClientFavorite.filter(userId => userId !== app.user.info.id)
              }
            })
          )

        const leadPatchResult =
          leadDetailQueryArgs &&
          dispatch(
            clientDetailApi.util.updateQueryData("getLeads", leadDetailQueryArgs, draftLeads => {
              draftLeads.data.forEach(e => {
                if (clientIds.includes(e.Client.id)) {
                  if (favorite) {
                    e.Client.isFavoriteOf.push(app.user.info.id)
                  } else {
                    e.Client.isFavoriteOf = e.Client.isFavoriteOf.filter(userId => userId !== app.user.info.id)
                  }
                }
              })
            })
          )

        try {
          await queryFulfilled
        } catch {
          listingPatchResult?.undo()
          detailPatchResult?.undo()
          leadPatchResult?.undo()
        }
      },
    }),

    addFeedback: build.mutation({
      query: feedBackValues => {
        const payload = {
          inquiryId: feedBackValues.lead?.id || feedBackValues?.values?.leadId,
          meeting: feedBackValues.values.selectMeeting,
          feedbackType: "cob",
          feedbackFields: feedBackValues.observationFields?.map(e => {
            return { typeId: e.id, rating: feedBackValues.values[e.value] }
          }),
          feedback: feedBackValues.values.feedback,
          attachments: feedBackValues?.files?.map(e => {
            return {
              name: e?.name,
              file: e?.url,
            }
          }),
          ...(!!feedBackValues?.values?.voiceComment && {
            commentVoiceRecording: {
              data: feedBackValues.values.voiceComment.split(",")[1],
              name: "voice-recording.webm",
              type: "audio/webm",
            },
          }),
        }
        return { url: `/feedbacks/inquiry`, method: "POST", body: payload }
      },
    }),

    callViaPropGo: build.mutation({
      query: values => {
        const payload = {
          cellNumber: values?.cellNumber,
          ...(values?.clientId && { clientId: values?.clientId }),
          ...(values?.userId && { userId: values?.userId }),
          affiliateId: values?.affiliateId,
          affiliate: values?.affiliate,
        }

        return {
          url: `appNotifications/callViaPropgo`,
          method: "POST",
          body: payload,
        }
      },
    }),

    addApplyForDiscount: build.mutation({
      query: payload => {
        let changeDiscount

        if (!payload.byApproval) {
          if (
            payload.data.discountValueType.value === "amount" &&
            payload.data.paymentCommitment.discountType === "percentage"
          ) {
            changeDiscount = ((payload.data.discount / payload.price) * 100).toFixed(6)
          } else if (
            payload.data.discountValueType.value === "percentage" &&
            payload.data.paymentCommitment.discountType === "amount"
          ) {
            changeDiscount = (payload.data.discount / 100) * payload.price
          } else {
            changeDiscount = payload.data.discount
          }
        } else {
          if (
            payload.data.discountValueType.value === "amount" &&
            payload.discountApprovalData.discountType === "percentage"
          ) {
            changeDiscount = ((payload.data.discount / payload.price) * 100).toFixed(6)
          } else if (
            payload.data.discountValueType === "percentage" &&
            payload.discountApprovalData.discountType === "amount"
          ) {
            changeDiscount = (payload.data.discount / 100) * payload.price
          } else {
            changeDiscount = payload.data.discount
          }
        }

        const data = !payload.byApproval
          ? {
              inquiryId: payload.leadData.id,
              discountType: "by_payment",
              paymentDiscountId: payload.data.paymentCommitment.discountId,
              discount: changeDiscount,
              requestedBy: payload.leadData.userId,
              ...(payload.reapply && { reapplyDiscount: true }),
            }
          : {
              inquiryId: payload.leadData.id,
              discountType: "by_approval",
              designationDiscountId: payload.discountApprovalData.discountId,
              comment: payload.data.reason,
              requestedFrom: payload.discountApprovalData.Approver.id,
              discount: changeDiscount,
              requestedBy: payload.discountApprovalData.Approver.userId,
              ...(payload.reapply && { reapplyDiscount: true }),
            }

        return {
          url: `/dealDiscounts`,
          method: "POST",
          body: data,
        }
      },
    }),

    getApplyDiscountListingInfoWithProject: build.query({
      query: id => `/dealDiscounts/${id}/listingInfoWithProject`,
      transformResponse: response => {
        const transformedData = discountTransformHelper(response)
        return transformedData
      },
      providesTags: ["project-info"],
    }),

    getExistingDiscount: build.query({
      query: id => `/dealDiscounts/${id}`,
      transformResponse: response => {
        const transformedData = existingDiscountTransformHelper(response)
        return transformedData
      },
    }),

    getAvailablePaymentDiscount: build.query({
      query: params => `/projects/${params.projectId}/paymentDiscounts?inquiryId=${params.inquiryId}`,
      transformResponse: response => availablePaymentDiscountTransformHelper(response),
    }),

    getDiscountApprovers: build.query({
      query: params =>
        `/users/discountApprovers?userId=${params.userId}&projectId=${params.projectId}&inquiryId=${params.inquiryId}`,
    }),

    changeAssignee: build.mutation({
      query: ({ clientIds, leadIds, type, filterRequired, queryObj, ...values }) => {
        let defaultFilterOptions = { pageLimit: 10, page: 0, sortBy: "id", orderBy: "desc" }
        const submitData = {
          body: {
            ...(values?.allocatedTo && {
              userIds: Array.isArray(values?.allocatedTo)
                ? values?.allocatedTo?.map(item => item.id)
                : [values?.allocatedTo]?.map(item => item?.id),
            }),
            byFilters: filterRequired ? filterRequired : false,
            ...(!filterRequired && { clientIds }),
            changeAssigneeProcess: values?.requestReAllocation
              ? "request_re_allocation"
              : values?.reallocatedType
                ? values?.reallocatedType
                : null,
            comments: `Client allocation has been changed through ${type.replace("-", " ")}.`,
            ...(values?.manualAllocationReason && { manualAllocationReason: values.manualAllocationReason }),
            ...(values?.reasonId && { clientReAllocationReasonId: values.reasonId }),
            ...(values?.userPhone && { cellNumber: values.userPhone }),
            ...(type == "search-lead" &&
              filterRequired && {
                leadFilters: getQueryTransformObject({ ...defaultFilterOptions, ...queryObj }, "desc", type),
              }),
            ...(type == "client-listings" &&
              filterRequired && {
                clientFilters: getQueryTransformObject({ ...defaultFilterOptions, ...queryObj }, "desc", type),
              }),
            ...(values?.referralType && { referralType: values?.referralType }),
            ...(values?.referralId && { referralId: values?.referralId }),
          },
          ...(!!leadIds?.length && { selfShare: "false" }),
        }
        return { url: "/clients/changeAssignee/v2", method: "PUT", body: submitData.body }
      },
      invalidatesTags: (resp, error, args) =>
        resp
          ? [
              ...args.clientIds.map(id => ({ type: "leads", id })),
              "client-detail",
              "clients",
              "leads",
              "todos",
              "allocationLogs",
              "editLogs",
            ]
          : [],
    }),

    getReviewObservationFields: build.query({
      query: () => `/feedbacks/cob/fields`,
      transformResponse: response => {
        const transformedResponse = reviewObservationFieldTransformHelper(response)
        return transformedResponse
      },
    }),

    getMeetings: build.query({
      query: taskId => `/internalInquiries/${taskId}/meetings`,
      transformResponse: response => {
        const transformedResponse = meetingTransformHelper(response)
        return transformedResponse
      },
    }),
    getReviewDetail: build.query({
      query: reviewId => `/feedbacks/review/${reviewId}/complete`,
      transformResponse: response => reviewDetailTransformHelper(response),
    }),

    getReasonForAllocation: build.query({
      query: () => `/clientReAllocationReasons`,
    }),

    addClientContactViewTracking: build.mutation({
      query: payload => ({ url: `/clientContactViewTrackings`, method: "POST", body: payload }),
    }),

    getStaffsWhoContactedClient: build.query({
      query: ({ page = 0, pageLimit = 10, clientIds }) =>
        `/whatsAppChats?clientId=${clientIds}&page=${page}&pageLimit=${pageLimit}&sortBy=lastMessageTimestamp&sortOrder=desc`,
    }),

    getClientStaffChat: build.query({
      query: params => {
        const { page = 0, pageLimit = 10, chatId } = params
        return `/whatsAppChats/${chatId}/messages?page=${page}&pageLimit=${pageLimit}&sortBy=messageTimestamp&sortOrder=desc`
      },
    }),
    getWhatsappChatInsights: build.query({
      query: params => `/whatsAppConversationInsights/${params?.chatId}/insights`,
      transformResponse: response => whatsappChatInsightsTransformer(response),
    }),

    getClientWhatsappInsights: build.query({
      query: ({ clientId }) => `/whatsAppConversationInsights/client/${clientId}/insights`,
      transformResponse: response => clientInsightsTransformer(response),
    }),

    getClientEngagementScores: build.query({
      query: ({ clientId, startDate, endDate }) =>
        `/whatsAppConversationInsights/clients/${clientId}/engagement-scores?${convertQueryObjToString({
          startDate,
          endDate,
        })}`,
      transformResponse: (response, meta, arg) => clientEngagementScoresTransformer(response, arg),
    }),

    getClientWhatsappOverallScores: build.query({
      query: ({ clientId }) => `/whatsAppConversationInsights/clients/${clientId}/overall-scores`,
      transformResponse: response => clientOverallScoresTransformer(response),
    }),

    getClientSegmentsWithSummary: build.query({
      query: ({ clientId }) => `/whatsAppConversationInsights/client/${clientId}/segments/with-summary`,
      transformResponse: response => clientSegmentsFilterTransformer(response),
    }),

    getClientConversationTimeline: build.query({
      query: ({ clientId, segmentId, page = 0, pageLimit = 10 }) =>
        `/whatsAppConversationInsights/client/${clientId}/segments/timeline?${convertQueryObjToString({
          segmentId: segmentId === "all" ? undefined : segmentId,
          page,
          pageLimit,
        })}`,
      transformResponse: response => clientConversationTimelineTransformer(response),
    }),
    getClientWorkflows: build.query({
      query: params => `/clients/workflowLogs/${params.clientId}?${convertQueryObjToString(params.filter)}`,
      transformResponse: response => ClientWorkflowsTransformer(response),
    }),
    getLeadCategories: build.query({
      query: params =>
        params?.isFilter ? `internalInquiryCategories` : `internalInquiryCategories?${convertQueryObjToString(params)}`,
      transformResponse: (response, query, args) =>
        args?.flagVisibility
          ? leadLabelsTransformer(response)
          : args?.isFilter
            ? leadsFilterCategoriesTransformer(response)
            : leadCategoriesTransformer(response),
    }),
  }),

  overrideExisting: false,
})

export const {
  useGetAllocationLogsQuery,
  useGetEditLogsQuery,
  useGetSharingLogsQuery,
  useGetCallLogsQuery,
  useGetCallDetailQuery,
  useGetTaskLogsQuery,
  useLazyGetTaskLogsQuery,
  useGetClientDetailQuery,
  useLazyGetClientDetailQuery,
  useGetLeadsQuery,
  useLazyGetLeadsQuery,
  useGetLeadCategoriesQuery,
  useGetClientDetailsQuery,
  useEditClientMutation,
  useAddLeadMutation,
  useAddShareWithSalesMutation,
  useAddUnShareWithSalesMutation,
  useShareLeadWithSupportAutoMutation,
  useShareLeadWithSupportManualMutation,
  useUnshareLeadFromSupportMutation,
  useAddFeedbackMutation,
  useAddApplyForDiscountMutation,
  useLazyGetApplyDiscountListingInfoWithProjectQuery,
  useCallViaPropGoMutation,
  useMarkFavoriteUnFavoriteMutation,
  useChangeAssigneeMutation,
  useGetReviewObservationFieldsQuery,
  useLazyGetReviewObservationFieldsQuery,
  useLazyGetMeetingsQuery,
  useLazyGetReviewDetailQuery,
  useLazyGetCurrentLeadSharingQuery,
  useGetExistingDiscountQuery,
  useLazyGetDiscountApproversQuery,
  useLazyGetAvailablePaymentDiscountQuery,
  useLazyGetExistingDiscountQuery,
  useGetReasonForAllocationQuery,
  useAddClientContactViewTrackingMutation,
  useGetStaffsWhoContactedClientQuery,
  useLazyGetStaffsWhoContactedClientQuery,
  useLazyGetClientStaffChatQuery,
  useGetClientStaffChatQuery,
  useGetClientWorkflowsQuery,
  useLazyGetClientWorkflowsQuery,
  useLazyGetWhatsappChatInsightsQuery,
  useGetWhatsappChatInsightsQuery,
  // Client-level WhatsApp Analysis hooks
  useGetClientWhatsappInsightsQuery,
  useLazyGetClientWhatsappInsightsQuery,
  useGetClientEngagementScoresQuery,
  useLazyGetClientEngagementScoresQuery,
  useGetClientSegmentsWithSummaryQuery,
  useLazyGetClientSegmentsWithSummaryQuery,
  useGetClientConversationTimelineQuery,
  useLazyGetClientConversationTimelineQuery,
  useGetClientWhatsappOverallScoresQuery,
  useLazyGetClientWhatsappOverallScoresQuery,
  useShareLeadWithFractionalAutoMutation,
  useShareLeadWithFractionalManualMutation,
} = clientDetailApi
//
